import { Archive } from 'lucide-react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Input, Select, Textarea } from '@platform/design-system';
import { useForm, useWatch } from 'react-hook-form';
import { FormSheet } from '../FormSheet.tsx';
import { usePageContext } from '../usePageContext.ts';
import {
  EXPENSE_LIMITS,
  expenseBodyOf,
  expenseFormOf,
  expenseFormSchema,
  type ExpenseFormValues,
} from './expense-form.ts';
import { subsidiaryOptions } from '../settings/subsidiaries.ts';
import type { Expense, ExpenseBody } from './finance.ts';
import styles from './Finance.module.css';

export type Subsidiary = { readonly id: string; readonly name: string; readonly archived: boolean };

export type ExpenseSheetProps = {
  expense: Expense | null;
  today: string;
  subsidiaries: readonly Subsidiary[];
  submitting: boolean;
  errorMessage: string | null;
  onClose: () => void;
  onSave: (body: ExpenseBody) => void;
  onToggleArchive: (expense: Expense) => void;
};

export const ExpenseSheet = ({
  expense,
  today,
  subsidiaries,
  submitting,
  errorMessage,
  onClose,
  onSave,
  onToggleArchive,
}: ExpenseSheetProps) => {
  const { t } = usePageContext();
  const form = useForm<ExpenseFormValues>({
    resolver: zodResolver(expenseFormSchema),
    defaultValues: expenseFormOf(expense, today),
    mode: 'onBlur',
  });
  const currentSubsidiary = useWatch({ control: form.control, name: 'subsidiaryId' });
  const { errors } = form.formState;

  return (
    <FormSheet
      open
      formId="expense-form"
      title={expense === null ? t('expenses.newTitle') : t('expenses.editTitle')}
      submitting={submitting}
      errorMessage={errorMessage}
      onClose={onClose}
      onSubmit={(event) => {
        void form.handleSubmit((values) => {
          onSave(expenseBodyOf(values));
        })(event);
      }}
    >
      <div className={styles['stack']}>
        <Input
          label={t('expenses.name')}
          required
          maxLength={EXPENSE_LIMITS.name}
          errorText={errors.name ? t('expenses.nameRequired') : undefined}
          {...form.register('name')}
        />
        <Input
          label={t('expenses.amount')}
          required
          inputMode="decimal"
          errorText={errors.amount ? t('expenses.amountInvalid') : undefined}
          {...form.register('amount')}
        />
        <Input
          label={t('expenses.spentOn')}
          required
          type="date"
          errorText={errors.spentOn ? t('expenses.dayRequired') : undefined}
          {...form.register('spentOn')}
        />
        <Select
          label={t('expenses.subsidiary')}
          options={[
            { value: '', label: t('expenses.wholeShop') },
            ...subsidiaryOptions(subsidiaries, currentSubsidiary),
          ]}
          {...form.register('subsidiaryId')}
        />
        <Textarea
          label={t('expenses.note')}
          rows={3}
          maxLength={EXPENSE_LIMITS.note}
          {...form.register('note')}
        />
        {expense === null ? null : (
          <div>
            <Button
              icon={<Archive size={20} aria-hidden="true" />}
              variant="outline"
              disabled={submitting}
              onClick={() => {
                onToggleArchive(expense);
              }}
            >
              {expense.archived ? t('expenses.restore') : t('expenses.archive')}
            </Button>
          </div>
        )}
      </div>
    </FormSheet>
  );
};
